/**
 * Incident Assistant Context
 * 
 * Provides shared state for the AI assistant sidebar across incident pages.
 * Holds incident data and accumulates investigation results.
 */

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import type {
  UserInvestigationResult,
  DeviceInvestigationResult,
} from '../data/types';
import { buildFullContext, type IncidentData, type InvestigationCache } from '../lib/incidentContextBuilder';

// ============================================================================
// TYPES
// ============================================================================

interface AssistantContextValue {
  // Incident data (set by IncidentDetail when it loads)
  incidentData: IncidentData | null;
  setIncidentData: (data: IncidentData) => void;

  // Investigation results (accumulated from IncidentInvestigation page)
  investigationCache: InvestigationCache;
  addUserInvestigation: (key: string, result: UserInvestigationResult) => void;
  addDeviceInvestigation: (key: string, result: DeviceInvestigationResult) => void;

  // Build full context for AI (includes incident + all investigations)
  buildAIContext: () => string;

  // Sidebar state
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Clear everything (when navigating away from incident)
  clearContext: () => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const IncidentAssistantContext = createContext<AssistantContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface IncidentAssistantProviderProps {
  children: ReactNode;
}

export function IncidentAssistantProvider({ children }: IncidentAssistantProviderProps) {
  const [incidentData, setIncidentDataState] = useState<IncidentData | null>(null);
  const [investigationCache, setInvestigationCache] = useState<InvestigationCache>({
    users: new Map(),
    devices: new Map(),
  });
  const [sidebarCollapsed, setSidebarCollapsedState] = useState(() => {
    // Persist sidebar state in localStorage
    const stored = localStorage.getItem('ai-assistant-sidebar-collapsed');
    return stored === 'true';
  });

  const setIncidentData = useCallback((data: IncidentData) => {
    setIncidentDataState(data);
  }, []);

  const addUserInvestigation = useCallback((key: string, result: UserInvestigationResult) => {
    setInvestigationCache(prev => {
      const newUsers = new Map(prev.users);
      newUsers.set(key, result);
      return { ...prev, users: newUsers };
    });
  }, []);

  const addDeviceInvestigation = useCallback((key: string, result: DeviceInvestigationResult) => {
    setInvestigationCache(prev => {
      const newDevices = new Map(prev.devices);
      newDevices.set(key, result);
      return { ...prev, devices: newDevices };
    });
  }, []);

  const setSidebarCollapsed = useCallback((collapsed: boolean) => {
    setSidebarCollapsedState(collapsed);
    localStorage.setItem('ai-assistant-sidebar-collapsed', String(collapsed));
  }, []);

  const clearContext = useCallback(() => {
    setIncidentDataState(null);
    setInvestigationCache({ users: new Map(), devices: new Map() });
  }, []);

  const buildAIContext = useCallback((): string => {
    if (!incidentData) {
      return 'No incident context available.';
    }
    return buildFullContext(incidentData, investigationCache);
  }, [incidentData, investigationCache]);

  const value: AssistantContextValue = {
    incidentData,
    setIncidentData,
    investigationCache,
    addUserInvestigation,
    addDeviceInvestigation,
    buildAIContext,
    sidebarCollapsed,
    setSidebarCollapsed,
    clearContext,
  };

  return (
    <IncidentAssistantContext.Provider value={value}>
      {children}
    </IncidentAssistantContext.Provider>
  );
}

// ============================================================================
// HOOK
// ============================================================================

export function useIncidentAssistant(): AssistantContextValue {
  const context = useContext(IncidentAssistantContext);
  if (!context) {
    throw new Error('useIncidentAssistant must be used within IncidentAssistantProvider');
  }
  return context;
}

/**
 * Safe version that returns null if not in provider
 * (useful for components that may or may not be in incident context)
 */
export function useIncidentAssistantSafe(): AssistantContextValue | null {
  return useContext(IncidentAssistantContext);
}
